import { IfAbility } from '@efdigital/ll-addon-auth';
import {
  CohortNavTemplate,
  ProgramNavTemplate,
} from '@efdigital/ll-addon-content';
import { EditContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { programTemplateParts } from '@efdigital/ll-admin-v2/components/parts.gts';
import ContentBreadcrumb from '@efdigital/ll-admin-v2/components/programs/content-breadcrumb.gts';
import { EditCohortVisibility } from '@efdigital/ll-admin-v2/components/programs/edit-cohort-visibility.gts';
import MessagesList from '@efdigital/ll-admin-v2/components/programs/messages/messages-list.gts';
import { Sidebar } from '@efdigital/ll-admin-v2/components/sidebar/sidebar.gts';
import { queryCohortMessages } from '@efdigital/ll-admin-v2/data/builders/xceed/messages';
import type { ProgramsProgramCohortContentAnnouncementsModel } from '@efdigital/ll-admin-v2/routes/programs/program/cohort/content/announcements';
import { array } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Request } from '@warp-drive/ember';
import { pageTitle } from 'ember-page-title';

interface Signature {
  Args: {
    model: ProgramsProgramCohortContentAnnouncementsModel;
  };
}

const parts = programTemplateParts;

export default class ProgramsProgramCohortContentAnnouncementsTemplate extends Component<Signature> {
  @tracked queryVersion = 0;

  get query() {
    // Include queryVersion to force re-query when incremented
    void this.queryVersion;
    return queryCohortMessages(this.args.model.cohort_id, 'announcement');
  }

  <template>
    {{pageTitle "Announcements"}}
    <ContentBreadcrumb @model={{@model}} />

    <div class="mb-5">
      <EditContext as |context|>
        <Sidebar>
          <:content>
            <ProgramNavTemplate
              @program={{@model.program}}
              @cohort={{@model.cohort}}
              @parts={{parts}}
            />
            <CohortNavTemplate
              @program={{@model.program}}
              @cohort={{@model.cohort}}
              @parts={{parts}}
            >
              <div class="container py-4">
                <h3 class="gud-eyebrow mb-4">Announcements</h3>

                <Request @query={{this.query}}>
                  <:content as |result|>
                    <MessagesList
                      @messages={{result.data}}
                      @category="announcement"
                      @programId={{@model.program_id}}
                      @cohortId={{@model.cohort_id}}
                    />
                  </:content>
                  <:loading>
                    <div class="d-flex justify-content-center py-5">
                      <div class="spinner-border" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  </:loading>
                  <:error as |error|>
                    <div class="alert alert-danger">
                      <iconify-icon inline icon="ph:warning" />
                      Failed to load announcements:
                      {{error.message}}
                    </div>
                  </:error>
                </Request>
              </div>
            </CohortNavTemplate>
          </:content>
          <:sidebar>
            <div class="px-3 py-4">
              <IfAbility @ability="messages" @atLeast="editor">
                <:if>
                  <div class="mb-5">
                    <h4 class="gud-eyebrow">Actions</h4>
                    <p class="form-text">Announcements are displayed in the
                      cohort portal and can be sent to participants. They can be
                      displayed immediately or scheduled for a future date.</p>
                    <p class="form-text">You can manage announcements in the
                      schedule section.</p>
                    <LinkTo
                      class="btn btn-primary w-100"
                      @route="programs.program.cohort.schedule"
                      @models={{array @model.program_id @model.cohort_id}}
                    >
                      Manage Announcements
                      <iconify-icon inline icon="ph:arrow-right" />
                    </LinkTo>
                  </div>
                </:if>
              </IfAbility>

              <EditCohortVisibility
                @item={{@model.cohort}}
                @context={{context}}
                class="mb-5"
              />
            </div>
          </:sidebar>
        </Sidebar>
      </EditContext>
    </div>
  </template>
}
